import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO Component
 *
 * Manages document head metadata using react-helmet-async.
 * Accepts props for all metadata fields and renders appropriate meta tags.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9, 2.10, 3.3
 */
const SEO = ({
  title,
  description,
  canonical,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  faqData,
  organizationSchema,
  searchSchema
}) => {
  const faqSchema =
    faqData && faqData.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData.map((item) => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer,
            },
          })),
        }
      : null;
  return (
    <Helmet>
      {/* Basic metadata - Requirements 2.1, 2.2 */}
      {title && <title>{title}</title>}
       {description && <meta name="description" content={description} />}
       {canonical && <link rel="canonical" href={canonical} />}
       {keywords && <meta name="keywords" content={keywords} />}
      {/* Open Graph metadata - Requirements 2.3, 2.4, 2.5, 2.6, 2.7 */}
      {ogTitle && <meta property="og:title" content={ogTitle || title} />}
      {ogDescription && <meta property="og:description" content={ogDescription || description} />}
      {ogType && <meta property="og:type" content={ogType} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Card metadata - Requirements 2.8, 2.9, 2.10 */}
      {twitterCard && <meta name="twitter:card" content={twitterCard} />}
      {twitterTitle && <meta name="twitter:title" content={twitterTitle || ogTitle || title} />}
      {twitterDescription && <meta
        name="twitter:description"
        content={twitterDescription || ogDescription || description}
      />}
       {/* FAQ Schema → Only if faqData is passed */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )},
      {organizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
      {searchSchema && (
        <script type="application/ld+json">
          {JSON.stringify(searchSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
